<template lang="pug">
.author-audio-jit-lexicons.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700.text-gray-900.dark_text-gray-100
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.dark_text-gray-100.group.rounded-md.p-2.text-sm.hover_bg-primary-100(href="#/home")
          | Home - all courses
    .ms-auto
    ToggleDark

  .flex-1.p-8.overflow-y-auto.overflow-x-hidden
    .max-w-screen-xl.mx-auto

      //- Access gate
      .rounded-lg.bg-red-50.dark_bg-red-900.border.border-red-300.dark_border-red-700.p-6.text-center(
        v-if="!isVinciAdmin"
      )
        p.text-red-700.dark_text-red-300.font-semibold Access restricted
        p.text-sm.mt-1.text-red-600.dark_text-red-400
          | This page requires a vinci_content administrator account.

      template(v-else)
        .prose.dark_prose-invert.max-w-none.mb-6
          h1.font-normal JIT Audio Lexicon Manager
          p.opacity-80
            | Upload and publish custom pronunciation lexicon files to S3.
            | Publishing invalidates the server-side JIT audio cache for the current environment.

        //- Status panel
        .p-5.bg-gray-100.dark_bg-gray-800.rounded-lg.mb-6
          .flex.items-center.justify-between.mb-3
            h2.m-0.text-lg.font-semibold Live status
            button.btn-hover_cursor-pointer.bg-gray-200.dark_bg-gray-700.btn-hover_bg-gray-300.dark_btn-hover_bg-gray-600.text-gray-900.dark_text-gray-100.font-medium.py-1.px-3.rounded.text-sm.border.border-gray-300.dark_border-gray-600(
              type="button"
              :disabled="statusLoading"
              @click="loadStatus"
            ) {{ statusLoading ? 'Loading…' : 'Refresh' }}

          p.text-red-500.dark_text-red-400.text-sm(v-if="statusError") {{ statusError }}

          .text-sm.opacity-70.mb-3(v-if="status")
            span.font-mono.bg-gray-200.dark_bg-gray-700.px-2.py-1.rounded.me-2 {{ status.env }}
            span.opacity-60 {{ status.bucket }}

          .overflow-x-auto(v-if="status && status.files.length > 0")
            table.w-full.text-sm.border-collapse
              thead
                tr.border-b.border-gray-300.dark_border-gray-600
                  th.text-left.py-2.pr-4 Locale
                  th.text-left.py-2.pr-4 Type
                  th.text-left.py-2.pr-4 Live revision
                  th.text-left.py-2 Last modified
              tbody
                tr.border-b.border-gray-200.dark_border-gray-700(
                  v-for="file in status.files"
                  :key="file.filename"
                )
                  td.py-2.pr-4.font-mono {{ file.locale }}
                  td.py-2.pr-4.opacity-70 {{ file.type }}
                  td.py-2.pr-4
                    span(v-if="file.exists && file.revision") rev {{ file.revision }}
                    span.opacity-40(v-else-if="file.exists") —
                    span.opacity-40(v-else) not published
                  td.py-2.opacity-70
                    | {{ file.lastModified ? new Date(file.lastModified).toLocaleString() : '—' }}

          p.text-sm.opacity-70.italic.mt-2(
            v-else-if="status && !statusLoading"
          )
            | No lexicons published yet for this environment. Upload and publish files below — the diff view will show an empty left pane until a live version exists.

          .text-sm.opacity-50.italic(v-else-if="!statusLoading && !statusError") No status loaded yet.

        //- Upload panel
        .p-5.bg-gray-100.dark_bg-gray-800.rounded-lg.mb-6
          h2.mt-0.mb-3.text-lg.font-semibold Upload lexicon files

          .border-2.border-dashed.border-gray-300.dark_border-gray-600.rounded-lg.p-6.text-center.cursor-pointer.bg-white.dark_bg-gray-900.mb-4(
            @click="openFilePicker"
            @drop.prevent="handleDrop"
            @dragover.prevent
          )
            input.hidden(
              type="file"
              accept=".xml,.json"
              multiple
              ref="fileInput"
              @change="handleFileInput"
            )
            p.font-semibold Drag files here or click to select
            p.text-xs.opacity-60.mt-1
              | Accepted: customlexicon_&lt;locale&gt;.xml · customlexicon_google_&lt;locale&gt;.json

          //- Per-file validation results
          .space-y-2.mb-4(v-if="selectedFiles.length > 0")
            .flex.items-start.gap-3.p-3.rounded-md.border(
              v-for="f in selectedFiles"
              :key="f.name"
              :class="fileStatusClass(f)"
            )
              .flex-none.text-lg {{ f.valid ? (hasRevisionChange(f) ? '✓' : '—') : '✗' }}
              .flex-1.min-w-0
                .font-mono.text-sm {{ f.name }}
                .text-xs.mt-1(
                  :class="f.valid ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'"
                )
                  template(v-if="f.valid")
                    | {{ f.type === 'azure' ? 'Azure XML' : 'Google JSON' }} · {{ f.locale }}
                    | · rev {{ f.revision }}
                    span.ms-3.opacity-60(v-if="liveRevisionFor(f)")
                      | live: {{ liveRevisionFor(f) }}
                      span.ms-1(v-if="f.revision !== liveRevisionFor(f)") → {{ f.revision }}
                      span.ms-1.text-orange-600.dark_text-orange-400(
                        v-if="f.revision === liveRevisionFor(f)"
                      ) (same as live)
                  template(v-else)
                    | {{ f.error }}

          p.text-sm.text-amber-700.dark_text-amber-300.mb-4(
            v-if="validFiles.length > 0 && publishableFiles.length === 0"
          )
            | All selected files match the live revision. Bump the revision in the file before publishing.

          //- Warning banner
          .flex.items-start.gap-3.rounded-md.bg-yellow-50.dark_bg-yellow-900.border.border-yellow-300.dark_border-yellow-700.p-4.mb-4(
            v-if="publishableFiles.length > 0"
          )
            .flex-none.text-yellow-600.dark_text-yellow-400.text-xl ⚠
            .text-sm.text-yellow-800.dark_text-yellow-200
              strong Publishing will invalidate the JIT audio cache for environment "{{ status?.env || '…' }}".
              |  Any cached audio for phrases affected by the updated lexicon will be regenerated on the next request.

          .flex.gap-3.items-center
            button.btn-hover_cursor-pointer.bg-primary-600.btn-hover_bg-primary-700.disabled_bg-gray-400.disabled_opacity-60.text-white.font-bold.py-2.px-5.rounded(
              type="button"
              :disabled="publishableFiles.length === 0 || publishing"
              @click="publish"
            ) {{ publishing ? 'Publishing…' : `Publish ${publishableFiles.length} file(s) to "${status?.env || '…'}"` }}
            button.btn-hover_cursor-pointer.bg-gray-200.dark_bg-gray-700.btn-hover_bg-gray-300.text-gray-900.dark_text-gray-100.font-medium.py-2.px-4.rounded.border.border-gray-300.dark_border-gray-600(
              type="button"
              :disabled="publishing"
              @click="clearFiles"
            ) Clear

          p.text-red-500.dark_text-red-400.text-sm.mt-3(v-if="publishError") {{ publishError }}

        //- Diff panel
        .space-y-6.mb-6(v-if="validFiles.length > 0")
          h2.mt-0.text-lg.font-semibold Diff — live vs selected

          .bg-gray-100.dark_bg-gray-800.rounded-lg.overflow-hidden(
            v-for="f in validFiles"
            :key="f.name"
          )
            .flex.items-center.justify-between.px-4.py-2.bg-gray-200.dark_bg-gray-700.border-b.border-gray-300.dark_border-gray-600
              span.font-mono.text-sm {{ f.name }}
              span.text-xs.opacity-60
                | left: live (via API) · right: selected file
            .h-64(
              :ref="el => setDiffRef(f.name, el)"
            )

        //- Result panel
        .p-5.bg-green-50.dark_bg-green-900.border.border-green-300.dark_border-green-700.rounded-lg(
          v-if="publishResult"
        )
          h2.mt-0.mb-3.text-lg.font-semibold.text-green-800.dark_text-green-200 Published successfully
          .text-sm.text-green-800.dark_text-green-200.space-y-1
            p
              strong Uploaded:
              |  {{ publishResult.uploaded.join(', ') }}
            p
              strong Cache invalidated:
              |  {{ publishResult.cacheInvalidated ? 'Yes' : 'No' }}
            p(v-if="publishResult.errors.length > 0")
              strong Skipped (validation errors):
              |  {{ publishResult.errors.join(' · ') }}
            p
              strong Updated manifest:
              div.mt-1.font-mono.text-xs.bg-green-100.dark_bg-green-800.p-2.rounded.whitespace-pre
                | {{ JSON.stringify(publishResult.manifest, null, 2) }}
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import * as monaco from 'monaco-editor';
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import useDocTitle from '../lib/useDocTitle';
import useDark from '../store/useDark';
import useJWT from '../lib/useJWT';
import {
  getJitLexiconStatus,
  getJitLexiconLiveContent,
  publishJitLexicons,
  type LexiconStatus,
  type LexiconPublishResult
} from '../lib/callModContentAuthors';

// ---------------------------------------------------------------------------
// Auth gate
// ---------------------------------------------------------------------------

const { jwt } = useJWT();
const isVinciAdmin = computed(() => jwt.value?.tenantid === 'vinci_content');

// ---------------------------------------------------------------------------
// Dark mode
// ---------------------------------------------------------------------------

const dark = useDark();

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

const status = ref<LexiconStatus | null>(null);
const statusLoading = ref(false);
const statusError = ref('');

async function loadStatus() {
  statusLoading.value = true;
  statusError.value = '';
  try {
    status.value = await getJitLexiconStatus();
  } catch (e: any) {
    statusError.value = e.message || 'Failed to load lexicon status.';
  } finally {
    statusLoading.value = false;
  }
}

onMounted(() => {
  if (isVinciAdmin.value) loadStatus();
});

// ---------------------------------------------------------------------------
// File validation (client-side)
// ---------------------------------------------------------------------------

const AZURE_XML_RE = /^customlexicon_([a-zA-Z]{2}-[a-zA-Z]{2,})\.xml$/i;
const GOOGLE_JSON_RE =
  /^customlexicon_google_([a-zA-Z]{2}-[a-zA-Z]{2,})\.json$/i;

interface ValidatedFile {
  name: string;
  file: File;
  valid: boolean;
  error?: string;
  type?: 'azure' | 'google';
  locale?: string;
  revision?: string;
  content?: string;
}

const selectedFiles = ref<ValidatedFile[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);

function extractXmlRevision(xml: string): string | null {
  const metaBlocks = xml.match(/<meta\b[^>]*\/?>/gi) ?? [];
  for (const block of metaBlocks) {
    if (!/\bname=["']revision["']/i.test(block)) continue;
    const m = block.match(/\bcontent=["']([^"']+)["']/i);
    if (m) return m[1];
  }
  return null;
}

async function validateFile(file: File): Promise<ValidatedFile> {
  const name = file.name;
  const content = await file.text();

  const azureMatch = AZURE_XML_RE.exec(name);
  if (azureMatch) {
    const locale = azureMatch[1];
    const revision = extractXmlRevision(content);
    if (!revision) {
      return {
        name,
        file,
        valid: false,
        error: 'Missing <meta name="revision"> element'
      };
    }
    const hasLexeme = /<lexeme\b/i.test(content);
    if (!hasLexeme) {
      return { name, file, valid: false, error: 'No <lexeme> entries found' };
    }
    return {
      name,
      file,
      valid: true,
      type: 'azure',
      locale,
      revision,
      content
    };
  }

  const googleMatch = GOOGLE_JSON_RE.exec(name);
  if (googleMatch) {
    const locale = googleMatch[1];
    try {
      const parsed = JSON.parse(content);
      const revision = parsed?.lexicon?.revision;
      if (!revision) {
        return {
          name,
          file,
          valid: false,
          error: 'Missing lexicon.revision field'
        };
      }
      const entries = parsed?.lexicon?.entries;
      if (!Array.isArray(entries) || entries.length === 0) {
        return { name, file, valid: false, error: 'No lexicon entries found' };
      }
      return {
        name,
        file,
        valid: true,
        type: 'google',
        locale,
        revision: String(revision),
        content
      };
    } catch {
      return { name, file, valid: false, error: 'Invalid JSON' };
    }
  }

  return {
    name,
    file,
    valid: false,
    error:
      'Filename must match customlexicon_<locale>.xml or customlexicon_google_<locale>.json'
  };
}

async function processFiles(files: File[]) {
  const results = await Promise.all(files.map(validateFile));
  // Merge with existing (deduplicate by name)
  const existing = new Map(selectedFiles.value.map(f => [f.name, f]));
  for (const r of results) existing.set(r.name, r);
  selectedFiles.value = [...existing.values()];
  // Re-render diffs after next tick
  await nextTick();
  renderDiffs();
}

const openFilePicker = () => fileInput.value?.click();

const handleFileInput = async (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (!input.files?.length) return;
  await processFiles([...input.files]);
  input.value = '';
};

const handleDrop = async (e: DragEvent) => {
  const files = [...(e.dataTransfer?.files ?? [])];
  if (files.length) await processFiles(files);
};

const clearFiles = () => {
  selectedFiles.value = [];
  disposeDiffs();
  publishResult.value = null;
  publishError.value = '';
};

const validFiles = computed(() => selectedFiles.value.filter(f => f.valid));

function liveRevisionFor(f: ValidatedFile): string | null {
  if (!status.value || !f.locale || !f.type) return null;
  const live = status.value.files?.find(
    file => file.locale === f.locale && file.type === f.type
  );
  if (live?.revision) return live.revision;
  if (f.type === 'google') {
    return status.value.googleRevisions?.[f.locale] ?? null;
  }
  return status.value.revisions?.[f.locale] ?? null;
}

/** True when the file is valid and its revision differs from live (or there is no live copy yet). */
function hasRevisionChange(f: ValidatedFile): boolean {
  if (!f.valid || f.revision == null) return false;
  const live = liveRevisionFor(f);
  if (live == null || String(live).trim() === '') return true;
  return String(f.revision).trim() !== String(live).trim();
}

const publishableFiles = computed(() =>
  validFiles.value.filter(hasRevisionChange)
);

function fileStatusClass(f: ValidatedFile): string {
  if (!f.valid) {
    return 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700';
  }
  if (!hasRevisionChange(f)) {
    return 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700';
  }
  return 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700';
}

// ---------------------------------------------------------------------------
// Monaco diff editors
// ---------------------------------------------------------------------------

const diffRefs = ref<Record<string, HTMLElement | null>>({});
const diffEditors = new Map<string, monaco.editor.IStandaloneDiffEditor>();

function setDiffRef(name: string, el: any) {
  diffRefs.value[name] = el as HTMLElement | null;
}

async function fetchLiveContentForDiff(filename: string): Promise<string> {
  if (!status.value?.env || !status.value?.bucket) return '';
  try {
    return await getJitLexiconLiveContent(filename);
  } catch {
    return '';
  }
}

function disposeDiffs() {
  for (const ed of diffEditors.values()) ed.dispose();
  diffEditors.clear();
}

async function renderDiffs() {
  if (!status.value?.env || !status.value?.bucket) return;

  disposeDiffs();
  const theme = dark.value ? 'vs-dark' : 'vs';

  for (const f of validFiles.value) {
    const el = diffRefs.value[f.name];
    if (!el) continue;

    const liveContent = await fetchLiveContentForDiff(f.name);
    const lang = f.type === 'azure' ? 'xml' : 'json';

    const originalModel = monaco.editor.createModel(liveContent, lang);
    const modifiedModel = monaco.editor.createModel(f.content ?? '', lang);

    const ed = monaco.editor.createDiffEditor(el, {
      automaticLayout: true,
      readOnly: true,
      renderSideBySide: true,
      theme,
      scrollBeyondLastLine: false,
      minimap: { enabled: false }
    });
    ed.setModel({ original: originalModel, modified: modifiedModel });
    diffEditors.set(f.name, ed);
  }
}

// Re-render diffs when dark mode or valid file list changes
watch(dark, () => {
  const theme = dark.value ? 'vs-dark' : 'vs';
  for (const ed of diffEditors.values()) {
    monaco.editor.setTheme(theme);
  }
});

watch([validFiles, status], async () => {
  await nextTick();
  renderDiffs();
});

onUnmounted(disposeDiffs);

// ---------------------------------------------------------------------------
// Publish
// ---------------------------------------------------------------------------

const publishing = ref(false);
const publishError = ref('');
const publishResult = ref<LexiconPublishResult | null>(null);

async function publish() {
  if (!publishableFiles.value.length) return;
  publishing.value = true;
  publishError.value = '';
  publishResult.value = null;
  try {
    const files = publishableFiles.value.map(f => f.file);
    publishResult.value = await publishJitLexicons(files);
    clearFiles();
    await loadStatus();
  } catch (e: any) {
    publishError.value = e.message || 'Publish failed.';
  } finally {
    publishing.value = false;
  }
}

// ---------------------------------------------------------------------------
// Title
// ---------------------------------------------------------------------------

useDocTitle(computed(() => 'JIT Lexicon Manager'));
</script>

<style scoped></style>
